import React from "react";
import { useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";

import { FetchAccountsPayload } from "@admin/api/account";
import { useFetchAccounts } from "@admin/hooks/useFetchAccounts";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import PageHeader from "@dashboard/shared/components/PageHeader";
import { alternatingBg, ContentContainer } from "@dashboard/shared/styles";
import { Paper, TableContainer, TableRow } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { withRequiredRole } from "@navigation/utils/withRequiredRole";
import { MinimalTable, MinimalTableCell, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import {
    createColumnHelper,
    flexRender,
    functionalUpdate,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    Updater,
    useReactTable,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper<API.Account>();
const tableColumns = [
    columnHelper.accessor("id", {
        id: "id",
        header: "Id",
        cell: (info) => <MinimalTableCell>{info.getValue()}</MinimalTableCell>,
        enableSorting: false,
    }),
    columnHelper.accessor("name", {
        id: "name",
        header: i18next.t("common:label.name"),
        cell: (info) => <MinimalTableCell>{info.getValue()}</MinimalTableCell>,
    }),
    columnHelper.accessor("email", {
        id: "email",
        header: i18next.t("common:label.email"),
        cell: (info) => <MinimalTableCell>{info.getValue()}</MinimalTableCell>,
    }),
];

const defaultParams: Required<FetchAccountsPayload> = {
    PageIndex: 0,
    PageSize: 12,
    SortCriteria: "email",
    SortDirection: 0,
    HasRoles: false,
    ExcludeDeleted: false,
};

const AdminAccountsPage = () => {
    const [params, setParams] = React.useState<Required<FetchAccountsPayload>>(defaultParams);
    const { data: accounts, isLoading, count: accountsCount } = useFetchAccounts(params);
    const theme = useTheme();

    const { t } = useTranslation(["pages"]);

    const onSortingChange = (updater: Updater<SortingState>) => {
        const newValue = functionalUpdate(updater, [])[0];
        setParams((prev) => ({ ...prev, SortCriteria: newValue.id, SortDirection: prev.SortDirection ? 0 : 1, PageIndex: 0 }));
    };

    const table = useReactTable<API.Account>({
        data: accounts,
        columns: tableColumns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: onSortingChange,
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <PageContainer>
            <PageHeader
                title={t("pages:adminAccountsPage.title")}
                iconName="fa-regular fa-user"
                description={t("pages:adminAccountsPage.description")}
            />

            <ContentContainer>
                <TableContainer component={Paper}>
                    <MinimalTable>
                        <MinimalTableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <Header key={header.id} {...header} />
                                    ))}
                                </TableRow>
                            ))}
                        </MinimalTableHeader>

                        <TableBody
                            table={table}
                            loading={isLoading}
                            noDataMessage={t("pages:adminAccountsPage.noUsersToDisplay")}
                        >
                            {table.getRowModel().rows.map((row, index) => (
                                <TableRow key={row.id} style={alternatingBg(index, theme)}>
                                    {row.getVisibleCells().map((cell) =>
                                        flexRender(cell.column.columnDef.cell, {
                                            ...cell.getContext(),
                                            key: cell.id,
                                        }),
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </MinimalTable>
                </TableContainer>
                <CustomTablePagination
                    component="div"
                    showFirstButton
                    showLastButton
                    rowsPerPageOptions={[12, 24, 48]}
                    count={accountsCount}
                    rowsPerPage={params.PageSize}
                    page={params.PageIndex}
                    onPageChange={(_event, page) => {
                        setParams((prev) => ({ ...prev, PageIndex: page }));
                    }}
                    onRowsPerPageChange={(e) => {
                        const nextSize = Number(e.target.value);
                        setParams((prev) => ({ ...prev, PageSize: nextSize, PageIndex: 0 }));
                    }}
                />
            </ContentContainer>
        </PageContainer>
    );
};

export default withRequiredRole(
    withMetaDescription(AdminAccountsPage, {
        title: i18next.t("pages:adminAccountsPage.metaTitle"),
        description: i18next.t("pages:adminAccountsPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaAdminRole],
);
